import React, { useContext, useEffect, useState } from 'react';
import OpportunitiesCarousel from '@components/Homepage/OpportunitiesCarousel';
import AuthContext from '@context/auth';
import texts from '@components/BondDirectoryFeature/en.json';
import useAuthStore from '@store/useAuthStore';
import { cn } from '@ui/atoms/utils/utils';
import { formatHomePageDeals } from '@components/Homepage/OpportunitiesCarousel/utils/helpers';
import { isValidResponseEntity } from '@helpers/utils';
import { getHomePageDisplayDeals } from '@services/homepage-deals-service';
import SearchResultsLoader from '@components/BondDirectoryFeature/components/SearchResultsLoader';
import { getCache, setCache } from '@utils/cache-utils';
import { CACHE_EXPIRY, CACHE_KEYS } from '@helpers/constants/cache-keys-expiry';
import useWindowDimensions from '@hooks/useWindowDimensions';

const BondDirectoryOpportunityCarousel = ({
    homepageDealsResponse,
    className,
}) => {
    const { width } = useWindowDimensions();
    const { isLogged } = useAuthStore();
    const { signUpModal } = useContext(AuthContext);

    const [homepageDeals, setHomePageDeals] = useState(
        homepageDealsResponse || []
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isWideScreen = width >= 768;

    const getFinalDeals = (formattedDeals) => {
        return formattedDeals.length <= 3 &&
            formattedDeals.length > 1 &&
            isWideScreen
            ? [...formattedDeals, ...formattedDeals]
            : formattedDeals;
    };

    const fetchAndProcessDeals = async () => {
        try {
            setLoading(true);
            setError(null);

            const cachedData = getCache(CACHE_KEYS.FEATURED_DEALS);
            if (cachedData) {
                setHomePageDeals(cachedData);
                return;
            }

            const response = await getHomePageDisplayDeals();
            if (!isValidResponseEntity(response)) {
                setError(response?.message || 'Invalid data response');
                return;
            }

            const formattedDeals = formatHomePageDeals(response?.entity);
            const finalDeals = getFinalDeals(formattedDeals);

            setCache(
                CACHE_KEYS.FEATURED_DEALS,
                finalDeals,
                CACHE_EXPIRY.FEATURED_DEALS
            );
            setHomePageDeals(finalDeals);
        } catch (err) {
            console.error('Error fetching featured bonds:', err);
            setError('Failed to fetch featured bonds.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAndProcessDeals();
    }, []);

    if (loading) {
        return (
            <section className={cn('relative', className)}>
                <SearchResultsLoader description="Loading featured bonds..." />
            </section>
        );
    }

    if (error || homepageDeals.length === 0) {
        return null;
    }

    return (
        <section className={cn('relative', className)}>
            <OpportunitiesCarousel
                signUpModal={signUpModal}
                isLogged={isLogged}
                opportunities={homepageDeals}
                title={texts.featuredBonds}
                showCta
                scaleFactor={0.75}
            />
        </section>
    );
};

export default BondDirectoryOpportunityCarousel;
